import { createRoot } from "react-dom/client";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router";
import { PublicCardErrorBoundary } from "./components/PublicCardErrorBoundary";
import { getDeviceId } from "./lib/deviceId";
import { isPublicCardPath } from "./lib/publicCardPath";

// Both roots are lazy, each behind its own `import()`, so a `/c/:token`
// scanner visit downloads only PublicBusinessCard's own small chunk (+ its
// scoped CSS) -- never the authenticated app's JS or its global CSS
// (Tailwind base/components/utilities, imported by App.tsx itself for
// exactly this reason). `AppRoot` (not `App` directly) is what's lazy here:
// it's the `Sentry.ErrorBoundary`-wrapped app, and its own `@sentry/react`
// import (SDK, tracing, Replay/rrweb) lives entirely inside that module --
// see AppRoot.tsx -- so it, too, never ships to a public-card visit. Neither
// branch imports the other's module graph eagerly.
const AppRoot = lazy(() => import("./AppRoot"));
const PublicBusinessCard = lazy(() => import("./pages/PublicBusinessCard"));

// Bootstrap correction (see plans/business-card-powered-by-hellooh.md, Work
// Unit 5): a `/c/:token` visitor is an anonymous phone scanner, not an
// authenticated app user. Merely adding <PublicBusinessCard> as another
// <Route> inside <App> would still mount PageConfigProvider,
// AppearanceProvider and production screen-protection -- AppearanceContext
// fetches `/user_settings` with whatever stale/garbage auth token happens to
// be sitting in localStorage, and `lib/api.ts` clears storage and redirects
// to /auth on a failed authenticated request. Neither may ever run for a
// public card view. So the root itself branches BEFORE any of that is
// imported-and-mounted: `/c/*` gets its own minimal React root (its own
// BrowserRouter/Routes, no providers, no Sentry), every other path keeps the
// existing app root untouched below.
//
// `isPublicCardPath` (src/lib/publicCardPath.ts) matches case-insensitively
// and treats a bare `/c` as public too, so `/C/...` or `/c` alone can never
// fall through to the authenticated shell -- only the exact route
// (`/c/:token`, matched inside the public root below) actually requires a
// token; a missing/malformed one renders PublicBusinessCard's own
// "unavailable" state instead.
const isPublicCardRoute = isPublicCardPath(window.location.pathname);

if (!isPublicCardRoute) {
  // Mint (or read) the stable device id on load so it exists before the
  // first request and rides on every /api/v1/* call via the X-Device-Id
  // header. Irrelevant to the public card's credential-free fetch, so
  // skipped entirely on that root.
  getDeviceId();
}

const container = document.getElementById("root");
const root = createRoot(container!);

if (isPublicCardRoute) {
  // No PageConfigProvider/AppearanceProvider/QueryClientProvider, no
  // ProtectedRoute -- a bare router straight to the public card.
  // `path="*"` (rather than only `/c/:token`) still renders
  // PublicBusinessCard for a bare `/c/` or an unexpected suffix; its own
  // token-shape validation turns that into the same "unavailable" state a
  // malformed token gets. `PublicCardErrorBoundary` (NOT Sentry's -- this
  // root never even imports `@sentry/react`, let alone reports telemetry)
  // catches a render throw from a malformed payload so a scanner gets the
  // same "couldn't load this card" message instead of a white screen;
  // `Suspense` covers the lazy chunk load.
  root.render(
    <PublicCardErrorBoundary>
      <Suspense fallback={null}>
        <BrowserRouter>
          <Routes>
            <Route path="/c/:token" element={<PublicBusinessCard />} />
            <Route path="*" element={<PublicBusinessCard />} />
          </Routes>
        </BrowserRouter>
      </Suspense>
    </PublicCardErrorBoundary>,
  );
} else {
  root.render(
    <Suspense fallback={null}>
      <AppRoot />
    </Suspense>,
  );
}
